import BusyIndicator from "@components/busyIndicator";
import { withBusyIndicator } from "@components/busyIndicator/withBusyIndicator";
import { IEditableAmountDialogData } from "@components/smart/selectionTableView/EditableAmountDialog";
import { IPairedItem } from "@components/smart/selectionTableView/SelectionTableView.utils";
import SelectionTableViewBase, { IProps } from "@components/smart/selectionTableView/SelectionTableViewBase";
import {
    ISmartODataTableAPI,
    NUM_ITEMS_PROGRESSBAR_THRESHOLD,
    TSmartODataTableStorage
} from "@components/smart/smartTable/SmartODataTableBase";
import { getRow, TableProgressBar, TCustomRowAction } from "@components/smart/smartTable/SmartTable.utils";
import { IRow, TId } from "@components/table";
import { IRowProps } from "@components/table/Rows.types";
import BindingContext, { IEntity } from "@odata/BindingContext";
import { getEntitySetByDocumentType } from "@odata/EntityTypes";
import { EntitySetName, IVatStatementDocumentEntity } from "@odata/GeneratedEntityTypes";
import { getVatCancellationAmountDefault } from "@pages/admin/vatRules/VatRules.utils";
import {
    AmountCancelReapplyPath,
    PairCancelReapplyTableCustomData
} from "@pages/electronicSubmission/CancelReapplyDef";
import {
    applyCancelOrReapplyDateOnDocuments,
    getCancelReapplyTranslation
} from "@pages/electronicSubmission/VatSubmission.utils";
import { memoizeOne } from "@utils/memoizeOne";
import React from "react";

import { RowAction, Status, ToggleState } from "../../enums";
import { processBatches, SLOW_REQUEST_OPTIONS_CONFIG } from "../../hooks/useBatchProcessor";
import { TableStorage } from "../../model/TableStorage";
import { getUtcDayjs } from "../../types/Date";

interface IState {
    done?: number;
    total?: number;
}

class CancelReapplyPairTableView extends SelectionTableViewBase<IProps, IState> {
    _actionIconName = "VatCancel";

    get args() {
        const tableStorage = (this.props.storage as unknown as TableStorage<ISmartODataTableAPI, PairCancelReapplyTableCustomData>);
        const { isProcessed, type } = tableStorage.getCustomData();
        return { isProcessed, type };
    }

    get secondaryTitle(): string {
        return null;
    }

    get selectionConfirmText(): string {
        return getCancelReapplyTranslation("Action", this.args);
    }

    get successMessage(): string {
        return getCancelReapplyTranslation("SuccessMessage", this.args);
    }

    get tNamespace(): string {
        return "ElectronicSubmission:CancelEditAmountDialog";
    }

    getRowEntitySet = (bc: BindingContext): EntitySetName => {
        const row = getRow(this.props.storage.tableAPI.getState().rows, bc);
        const doc = row.customData.entity as IVatStatementDocumentEntity;
        return getEntitySetByDocumentType(doc.DocumentTypeCode);
    };

    getCustomRowAction = memoizeOne((): TCustomRowAction => ({
        actionType: RowAction.Check,
        isSingleSelect: false,
        onClick: this.handleRowActionClick,
        onToggleChange: this.handleToggleChange,
    }));

    getSelectedRows = memoizeOne(() => {
        const oldBc = this.props.storage.data.rowBindingContext;
        const bc = oldBc?.createNewBindingContext(this.props.storage.data.bindingContext.getPath()).addKey(oldBc.getKey());

        return bc ? [bc] : [];
    }, () => [this.props.storage.data.rowBindingContext]);

    handleRowSelect = (bindingContext: BindingContext): void => {
        const correctBc = bindingContext.createNewBindingContext(this.getRowEntitySet(bindingContext)).addKey(bindingContext.getKey());
        this.props.onRowSelect?.(correctBc, null, {});
    };

    shouldComponentUpdate(nextProps: Readonly<IProps>, nextState: Readonly<IState>): boolean {
        return super.shouldComponentUpdate(nextProps, nextState)
                || this.state.done !== nextState.done || this.state.total !== nextState.total;
    }

    _batchSize = 1;
    reportProgress = (done: number, total: number) => {
        if (total / this._batchSize > NUM_ITEMS_PROGRESSBAR_THRESHOLD) {
            this.setState({ done, total });
        }
    }

    handleDocumentSelection = async (selected: BindingContext[], entities: IEntity[], storage?: TSmartODataTableStorage) => {
        const { period, type, isProcessed } = (this.props.storage as unknown as TableStorage<ISmartODataTableAPI, PairCancelReapplyTableCustomData>).getCustomData();
        const args = {
            period: {
                from: getUtcDayjs(period.from),
                to: getUtcDayjs(period.to)
            },
            type: type,
            isReversal: isProcessed
        };

        this.reportProgress(0, entities.length);

        const errors: string[] = [];
        await processBatches((entities as IVatStatementDocumentEntity[]), async (batch, batchIndex) => {
            const error = await applyCancelOrReapplyDateOnDocuments(storage.oData, {
                ...args,
                changedDocuments: batch
            }, storage);
            if (error !== true) {
                errors.push(error);
            }
        }, SLOW_REQUEST_OPTIONS_CONFIG, this.reportProgress);

        this.setState({ done: null, total: null });

        if (errors?.length) {
            // filter unique errors
            const errorSet = new Set(errors);
            // show error message
            storage.setTableAlert({
                status: Status.Error,
                title: storage.t("Common:Errors.ErrorHappened"),
                subTitle: errorSet.values()
            });
            return false;
        }
        this.props.onSelection(selected, entities, storage);
        return true;
    }

    async handleSelection(selected: BindingContext[], entities: IEntity[], storage?: TSmartODataTableStorage): Promise<boolean | void> {
        const ret = await this.handleDocumentSelection(selected, entities, storage);
        // we don't want to keep selection after confirm
        this._selectedItems = [];
        this.storage.tableAPI?.setState({ activeRows: new Set() });
        this.storage.setCustomData({ pairedItems: {} });
        return ret;
    }

    // pairing with Amount
    getEditableAmountDialogData(row: IRow, pairedDialogItemId: string): IEditableAmountDialogData {
        const { storage } = this;
        const { pairedItems } = storage.getCustomData();
        const item = row.customData.entity as IVatStatementDocumentEntity;
        const pairedItem = pairedItems[pairedDialogItemId];
        return {
            TransactionAmount: pairedItem.TransactionAmount, // current value
            NumberOurs: item.NumberOurs,
            TransactionCurrency: item.Currency,
            Currency: item.Currency,
            ExchangeRatePerUnit: 1, // for VAT Statement we work only with base currency
            TotalAmount: item.AmountVat // max amount
        };
    }

    async updateRowAmount(rowId: TId, row: IRowProps): Promise<void> {
        const { storage } = this;
        const { entity } = row.customData;

        let { pairedItems } = storage.getCustomData();
        if (!pairedItems) {
            pairedItems = {};
        }
        const key = rowId.toString();
        const affectedRows = await this.props.storage.tableAPI.getAffectedRows();
        const isSelected = !!affectedRows.find(row => row.bc.toString() === key);

        // update pairedItems in place
        this.updatePairedItem(pairedItems, key, isSelected, entity);
        storage.setCustomData({ pairedItems: { ...(pairedItems ?? {}) } });
    }

    updatePairedItem(pairedItems: Record<string, IPairedItem>, key: string, isSelected: boolean, entity: IVatStatementDocumentEntity) {
        if (isSelected) {
            // row is selected -> unselect, clear data
            if (pairedItems?.[key]) {
                delete pairedItems[key];
            }
        } else {
            // row is not selected -> select, set data
            pairedItems[key] = {
                TransactionAmount: getVatCancellationAmountDefault(entity),
                ExchangeRatePerUnit: 1,
                TransactionCurrency: entity?.Currency,
                Amount: entity?.AmountVat
            };
        }
    }

    handleToggleChange = (toggleState: ToggleState): true => {
        const { storage } = this;
        const hasCancellationAmount = !!storage.data.definition.columnDefinition[AmountCancelReapplyPath];
        if (hasCancellationAmount) {
            let { pairedItems } = storage.getCustomData();
            if (toggleState === ToggleState.AllUnchecked) {
                pairedItems = {};
            } else if (toggleState === ToggleState.AllChecked) {
                if (!pairedItems) {
                    pairedItems = {};
                }
                const allRows = storage.tableAPI.getState().rows;
                Object.entries(allRows).forEach((tupple) => {
                    const [rowId, row] = tupple;
                    if (!pairedItems[rowId]) {
                        this.updatePairedItem(pairedItems, rowId, false, row.customData.entity as IVatStatementDocumentEntity);
                    }
                });
            }
            storage.setCustomData({ pairedItems: { ...(pairedItems ?? {}) } });
            storage.tableAPI.forceUpdate();
        }
        return true;
    }

    handleRowActionClick = async (rowId: TId, row: IRowProps): Promise<void> => {
        const { storage } = this;
        const hasCancellationAmount = !!storage.data.definition.columnDefinition[AmountCancelReapplyPath];

        if (hasCancellationAmount) {
            await this.updateRowAmount(rowId, row);
            storage.tableAPI.forceUpdate();
        }
    }

    renderBusyIndicator() {
        const { done, total } = this.state;
        if (this.props.busy && total > 1) {
            return (
                <BusyIndicator
                    ignoreInfiniteBusyIndicatorDetection
                    customContent={(
                        <TableProgressBar done={done} total={total} />
                    )}/>
            );
        }
        return super.renderBusyIndicator();
    }
}

export default withBusyIndicator({ passBusyIndicator: true })(CancelReapplyPairTableView);
